import MainTable from '@Components/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import { WrappedTableCell } from '@Components/UI/Tables/Tables.styled';
import { egp, MethodSummaryCell } from '@ContentManagement/EnrollmentInvoices';
import { TableHeader } from '@Features/generics/generics.type';
import {
	Box,
	Icon,
	TableCell,
	TableRow,
	ToggleButton,
	ToggleButtonGroup,
} from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import { GraphSlice } from '@Services/classrooms/classrooms.res.types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
	analyticsSeasonStart,
	HUB_METHODS,
	HUB_METHOD_LABELS,
	pickMethodColor,
} from '../Constants/methods';
import {
	downloadCsvResponse,
	FamilyAnalyticRequest,
	FamilyGraphRequest,
	FamilyRevenueRequest,
} from '../Services/Analytics.req';
import {
	Distribution,
	FamilyKey,
	MethodBucketList,
	PaginationMeta,
	RevenueSummary,
} from '../Types/Analytics.types';
import DistributionDonuts from './DistributionDonuts';
import HubDateRange, { HubRange } from './HubDateRange';
import MethodGraphSection from './MethodGraphSection';
import { EnrollmentSummaryCards, RevenueSummaryCards } from './SummaryCards';
import WrappedHeadsTable from './WrappedHeadsTable';

type ViewKey = 'analytics' | 'revenue';

export type FamilyTabConfig = {
	family: FamilyKey;
	entityLabel: string;
	/** row click target; null disables navigation (flag off / no page) */
	entityRoute?: (row: any) => string | null;
	showPrice?: boolean;
	showViaBundle?: boolean;
	showCoursesCount?: boolean;
	revenueFootnote?: string;
	/** extra content under the table (Classrooms: learning-time grid) */
	footer?: React.ReactNode;
};

/**
 * The standard family tab: [Analytics | Revenue] switch, KPI cards, export,
 * MainTable, distribution donuts, method graph. The three family tabs and the
 * instructor drill-down are configs over this one component.
 */
const FamilyStatsTab: React.FC<FamilyTabConfig & { instructorId?: number }> = ({
	family,
	entityLabel,
	entityRoute,
	showPrice,
	showViaBundle,
	showCoursesCount,
	revenueFootnote,
	footer,
	instructorId,
}) => {
	const history = useHistory();
	const { displaySnackbar } = useSnackbar();
	const [view, setView] = useState<ViewKey>('analytics');
	const [rows, setRows] = useState<any[]>([]);
	const [meta, setMeta] = useState<PaginationMeta | null>(null);
	const [countSummary, setCountSummary] = useState<MethodBucketList | null>(null);
	const [viaBundleCount, setViaBundleCount] = useState<number | undefined>(undefined);
	const [revenueSummary, setRevenueSummary] = useState<RevenueSummary | null>(null);
	const [distribution, setDistribution] = useState<Distribution>({
		enrollments: [],
		revenue: [],
	});
	// One date control per tab (the Overview's HubDateRange), season default;
	// it drives the cards, table and donuts together.
	const seasonRange = useMemo(() => ({ from: analyticsSeasonStart() }), []);
	const [range, setRange] = useState<HubRange>(seasonRange);
	const lastArgsRef = useRef<Partial<onPageChangeRequestMeta>>({});

	const analyticHeads = useMemo<TableHeader[]>(() => {
		const heads: TableHeader[] = [
			{ displayName: entityLabel, fieldName: 'name', canSort: true, width: '16%' },
			{ displayName: 'Instructor', fieldName: 'instructor_name', canSort: true, width: '12%' },
		];
		if (showPrice) heads.push({ displayName: 'Price', fieldName: 'price', canSort: true });
		if (showCoursesCount)
			heads.push({ displayName: 'Courses', fieldName: 'courses_count', canSort: true });
		HUB_METHODS.forEach((method) =>
			heads.push({
				displayName: HUB_METHOD_LABELS[method],
				fieldName: method,
				canSort: true,
			})
		);
		if (showViaBundle)
			heads.push({ displayName: 'Via Bundle', fieldName: 'via_bundle', canSort: true });
		heads.push({ displayName: 'Total', fieldName: 'total', canSort: true });
		return heads;
	}, [entityLabel, showPrice, showCoursesCount, showViaBundle]);

	const revenueHeads = useMemo<TableHeader[]>(() => {
		const heads: TableHeader[] = [
			{ displayName: entityLabel, fieldName: 'name', canSort: true, width: '16%' },
			{ displayName: 'Instructor', fieldName: 'instructor_name', canSort: true, width: '12%' },
		];
		HUB_METHODS.forEach((method) =>
			heads.push({
				displayName: HUB_METHOD_LABELS[method],
				fieldName: method,
				canSort: true,
			})
		);
		heads.push({ displayName: 'Enrollments', fieldName: 'enrollments', canSort: true });
		heads.push({ displayName: 'Total Sales', fieldName: 'grand_total', canSort: true });
		heads.push({ displayName: 'Refunded', fieldName: 'refunded', canSort: false });
		heads.push({ displayName: 'Total Revenue', fieldName: 'net', canSort: false });
		return heads;
	}, [entityLabel]);

	const load = useCallback(
		async (request: Partial<onPageChangeRequestMeta> = {}, requestView?: ViewKey) => {
			const activeView = requestView || view;
			lastArgsRef.current = request;
			const payload = {
				page: request.activePage || 1,
				perPage: request.perPage || 10,
				query: request.query || '',
				sortBy: request.sortBy?.field ? request.sortBy : undefined,
				from: range.from,
				to: range.to,
				instructor_id: instructorId,
			};
			try {
				const response =
					activeView === 'analytics'
						? await FamilyAnalyticRequest(family, payload)
						: await FamilyRevenueRequest(family, payload);
				const data = response.data.data || {};
				setRows(data.rows || []);
				setMeta(response.data.meta || null);
				setDistribution(data.distribution || { enrollments: [], revenue: [] });
				if (activeView === 'analytics') {
					setCountSummary({
						purchase_methods: HUB_METHODS.map((method) => ({
							purchase_method: method,
							purchase_method_count: Number(data.summary?.[method] ?? 0),
						})),
						purchase_methods_total: Number(data.summary?.total ?? 0),
					});
					setViaBundleCount(showViaBundle ? Number(data.summary?.via_bundle ?? 0) : undefined);
				} else {
					setRevenueSummary(data.summary || null);
				}
			} catch (error: any) {
				displaySnackbar(
					'error',
					error.response?.data?.message || `Unable to load ${entityLabel} statistics`
				);
			}
		},
		[displaySnackbar, entityLabel, family, instructorId, range, showViaBundle, view]
	);

	useEffect(() => {
		load({ activePage: 1, perPage: 10 }, view);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [view, instructorId, range]);

	const exportRows = async () => {
		const request = lastArgsRef.current;
		const payload = {
			query: request.query || '',
			sortBy: request.sortBy?.field ? request.sortBy : undefined,
			from: range.from,
			to: range.to,
			instructor_id: instructorId,
			export: true,
		};
		try {
			const response =
				view === 'analytics'
					? await FamilyAnalyticRequest(family, payload)
					: await FamilyRevenueRequest(family, payload);
			downloadCsvResponse(response, `${family}-${view}`);
		} catch {
			displaySnackbar('error', 'Unable to export');
		}
	};

	const fetchGraph = useCallback(
		async (params: { slice: string; from: Date; to: Date }) => {
			const response = await FamilyGraphRequest(family, {
				...params,
				instructor_id: instructorId,
			});
			return response.data.data || [];
		},
		[family, instructorId]
	);

	const navigate = (row: any) => {
		const target = entityRoute?.(row);
		if (target) history.push(target);
	};

	const renderAnalyticsRow = (row: any) => (
		<ClickableRow key={row.product_id} hover onClick={() => navigate(row)}>
			<WrappedTableCell align="center">{row.name}</WrappedTableCell>
			<WrappedTableCell align="center">{row.instructor_name || '-'}</WrappedTableCell>
			{showPrice && <TableCell align="center">{row.price ?? '-'}</TableCell>}
			{showCoursesCount && <TableCell align="center">{row.courses_count ?? 0}</TableCell>}
			{HUB_METHODS.map((method) => (
				<TableCell key={method} align="center">
					{row[method] ?? 0}
				</TableCell>
			))}
			{showViaBundle && <TableCell align="center">{row.via_bundle ?? 0}</TableCell>}
			<TableCell align="center">{row.total ?? 0}</TableCell>
		</ClickableRow>
	);

	const renderRevenueRow = (row: any) => (
		<ClickableRow key={row.product_id} hover onClick={() => navigate(row)}>
			<WrappedTableCell align="center">{row.name}</WrappedTableCell>
			<WrappedTableCell align="center">{row.instructor_name || '-'}</WrappedTableCell>
			{HUB_METHODS.map((method) => (
				<TableCell key={method} align="center">
					<MethodSummaryCell
						summary={{
							count: row[`${method}_count`],
							total: row[`${method}_total`],
						}}
						isFree={method === 'free'}
					/>
				</TableCell>
			))}
			<TableCell align="center">{row.enrollments ?? 0}</TableCell>
			<TableCell align="center">{egp(row.grand_total)}</TableCell>
			<TableCell align="center">{egp(row.refunded)}</TableCell>
			<TableCell align="center">{egp(row.net)}</TableCell>
		</ClickableRow>
	);

	const heads = view === 'analytics' ? analyticHeads : revenueHeads;

	return (
		<TabPage>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb="1.5rem">
				<ToggleButtonGroup
					value={view}
					exclusive
					size="small"
					onChange={(_, nextView) => nextView && setView(nextView)}
				>
					<ToggleButton value="analytics">Analytics</ToggleButton>
					<ToggleButton value="revenue">Revenue</ToggleButton>
				</ToggleButtonGroup>
				<Box display="flex" alignItems="center" gap="1rem">
					<HubDateRange initialRange={seasonRange} onApply={(nextRange) => setRange(nextRange)} />
					<RoundButton onClick={exportRows} startIcon={<Icon>south</Icon>} variant="contained">
						Export
					</RoundButton>
				</Box>
			</Box>

			{view === 'analytics' ? (
				<EnrollmentSummaryCards summary={countSummary} viaBundle={viaBundleCount} />
			) : (
				<RevenueSummaryCards summary={revenueSummary} />
			)}

			<WrappedHeadsTable>
			<MainTable
				tableFilters={[]}
				RowsperPage={meta?.per_page || 10}
				onPageChangeRequest={load}
				totalPages={meta?.last_page || 1}
				total={meta?.total || 0}
				pageNum={meta?.current_page || 1}
				tableHeads={heads}
				// The tab's HubDateRange is the single date control.
				dateFilter={false}
				// The revenue view stacks two-line money cells under 13 heads —
				// without the extra width the header labels collide.
				tableMinWidth={view === 'revenue' ? '110rem' : '85rem'}
				renderItems={() => {
					if (!rows.length) {
						return (
							<TableRow>
								<TableCell colSpan={heads.length}>
									<NoResults />
								</TableCell>
							</TableRow>
						);
					}
					return rows.map(view === 'analytics' ? renderAnalyticsRow : renderRevenueRow);
				}}
			/>
			</WrappedHeadsTable>
			{view === 'revenue' && revenueFootnote && <Footnote>{revenueFootnote}</Footnote>}
			{view === 'revenue' && (
				<Footnote>
					Revenue is computed from financial snapshots (coverage start date on the
					Overview); enrollment counts cover all history. Scratchcard money is valued at
					list price. Refunds are netted by refund date — a refund in this period may
					correspond to a sale outside it.
				</Footnote>
			)}

			<DistributionDonuts
				enrollments={distribution.enrollments}
				revenue={distribution.revenue}
				revenueTitle="Total Revenue share"
			/>

			<MethodGraphSection
				fetchSeries={fetchGraph}
				colors={(line: GraphSlice) => pickMethodColor(String(line.id))}
			/>
			{footer}
		</TabPage>
	);
};

const TabPage = styled.div`
	width: 100%;
	max-width: 100%;
	min-width: 0;
`;

const ClickableRow = styled(TableRow)`
	cursor: pointer;
`;

const Footnote = styled.div`
	font-size: 0.8rem;
	color: rgba(0, 0, 0, 0.55);
	margin-top: 0.5rem;
`;

export default FamilyStatsTab;
